/**
 * Form primitives shared by the Application Entities and Listener editors.
 *
 * Labels carry the units and the consequence, errors sit against the field
 * that caused them, and a checkbox always states what it does — not just what
 * it is called.
 */

import type { ChangeEvent, ReactNode } from 'react'

export function FormSection({
  title,
  description,
  tone,
  children,
}: {
  title: string
  description?: ReactNode
  tone?: 'inbound' | 'outbound' | 'plain'
  children: ReactNode
}) {
  return (
    <fieldset className={`form-section form-section-${tone ?? 'plain'}`}>
      <legend>{title}</legend>
      {description && <div className="form-section-note">{description}</div>}
      {children}
    </fieldset>
  )
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string
  hint?: ReactNode
  error?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className={error ? 'field field-invalid' : 'field'}>
      <span className="field-label">
        {label}
        {required && <span className="field-required"> *</span>}
      </span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  )
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  maxLength,
  placeholder,
  type = 'text',
  showCount,
  disabled,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: ReactNode
  error?: string
  required?: boolean
  maxLength?: number
  placeholder?: string
  type?: string
  showCount?: boolean
  disabled?: boolean
}) {
  const counted =
    showCount && maxLength ? (
      <>
        {hint}
        {hint ? ' ' : ''}
        <span className="char-count">
          {value.length}/{maxLength}
        </span>
      </>
    ) : (
      hint
    )

  return (
    <Field label={label} hint={counted} error={error} required={required}>
      <input
        type={type}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      />
    </Field>
  )
}

export function TextArea({
  label,
  value,
  onChange,
  hint,
  maxLength,
  rows = 3,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: ReactNode
  maxLength?: number
  rows?: number
}) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={value}
        rows={rows}
        maxLength={maxLength}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
      />
    </Field>
  )
}

export function CheckField({
  label,
  detail,
  checked,
  onChange,
  disabled,
}: {
  label: string
  detail?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className={disabled ? 'check check-disabled' : 'check'}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      />
      <span>
        <span className="check-label">{label}</span>
        {detail && <span className="check-detail">{detail}</span>}
      </span>
    </label>
  )
}

/**
 * A statement of behaviour the operator cannot change from this form. Used for
 * the permissions that are open by policy, so nobody reads their absence from
 * the checkbox list as "off".
 */
export function FixedRule({ label, detail }: { label: string; detail: ReactNode }) {
  return (
    <div className="fixed-rule">
      <span className="fixed-rule-mark">always on</span>
      <span>
        <span className="check-label">{label}</span>
        <span className="check-detail">{detail}</span>
      </span>
    </div>
  )
}
